import type { z } from "zod";
import { ApiError, HealthResponse } from "@empire/shared";

const BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

/**
 * The two ways a request can fail, kept apart on purpose.
 *
 * "The server said no" and "there was no server" look identical to `fetch` —
 * both end up as a rejected promise if you are careless — but they mean
 * opposite things to a defender. One says *your maze is wrong*; the other says
 * *your maze is fine, the network isn't*. Showing the first message for the
 * second case is how you get someone redrawing a perfectly legal defence
 * because the train went into a tunnel.
 */

/** The server answered, and refused. */
export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiProblem";
  }
}

/** Nothing answered: offline, asleep, DNS, CORS, a cancelled request. */
export class Unreachable extends Error {
  constructor(
    readonly path: string,
    override readonly cause?: unknown
  ) {
    super(`could not reach the server (${path})`);
    this.name = "Unreachable";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  /** Bearer token, for endpoints behind a session. */
  token?: string | null;
  signal?: AbortSignal;
}

/**
 * Every response is parsed by the same Zod schema the server validates against.
 *
 * The third type argument pins the schema's INPUT to `unknown`, which is what a
 * response body actually is. Without it, a schema with a `.default()` — the
 * layout's wall and guild levels, for instance — makes TypeScript infer the
 * shape you are allowed to *send*, where those fields are optional, rather than
 * the shape you get *back*, where they are always present.
 */
export async function request<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  options: RequestOptions = {}
): Promise<T> {
  const { method = "GET", body, token, signal } = options;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      signal,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    throw new Unreachable(path, cause);
  }

  if (!res.ok) throw await problemFrom(res, path);
  return schema.parse(await res.json());
}

/**
 * Turn a failed response into an ApiProblem.
 *
 * The server sends `{ code, message, details }` for everything it refuses on
 * purpose, so the defender sees the server's own words. A response that is not
 * shaped like that (a proxy's HTML error page, say) still has to produce
 * something, so the status line stands in — but it is clearly not our message.
 */
async function problemFrom(res: Response, path: string): Promise<ApiProblem> {
  try {
    const parsed = ApiError.parse(await res.json());
    return new ApiProblem(res.status, parsed.code, parsed.message, parsed.details);
  } catch {
    return new ApiProblem(res.status, "HTTP_ERROR", `${res.status} ${res.statusText} for ${path}`);
  }
}

export function getHealth(): Promise<z.infer<typeof HealthResponse>> {
  return request("/healthz", HealthResponse);
}
